// Premium Propulse Memory Match — 3D flipping holographic cards, combo counter,
// confetti on match, fireworks on perfect run, learning insight modal after each match.
import React, { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, Volume2, VolumeX, Sparkles, Lightbulb, Trophy, Zap } from 'lucide-react';
import { GameFrame, AnnouncementBanner, ScoreTile, playGameSound, GoldButton } from './shared/GameFX';
import { ConfettiBurst, Fireworks } from './ludo/LudoConfetti';

function shuffle(a) {
    const arr = [...a];
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
}

// Per-card insight (auto-generated if not present in seed)
const FALLBACK_INSIGHTS = {
    default: 'Strong product knowledge separates trusted electricians from the rest. Keep going!',
};

export default function MemoryMatch({ game, onPlayed }) {
    const baseCards = game.config?.cards || [];
    const [deck, setDeck] = useState([]);
    const [flipped, setFlipped] = useState([]);
    const [matched, setMatched] = useState([]);
    const [moves, setMoves] = useState(0);
    const [combo, setCombo] = useState(0);
    const [bestCombo, setBestCombo] = useState(0);
    const [score, setScore] = useState(0);
    const [done, setDone] = useState(false);
    const [muted, setMuted] = useState(false);
    const [announce, setAnnounce] = useState(null);
    const [bursts, setBursts] = useState([]);
    const [showFireworks, setShowFireworks] = useState(false);
    const [insight, setInsight] = useState(null);
    const burstIdRef = useRef(0);

    const triggerBurst = () => {
        burstIdRef.current += 1;
        const id = burstIdRef.current;
        setBursts((b) => [...b, { id, count: 25, scale: 1.0 }]);
        setTimeout(() => setBursts((b) => b.filter((x) => x.id !== id)), 1600);
    };

    const announce_ = (text, tone = 'green') => {
        const id = Date.now() + Math.random();
        setAnnounce({ text, tone, id });
        setTimeout(() => setAnnounce((a) => (a && a.id === id ? null : a)), 1500);
    };

    const reset = () => {
        const pairs = baseCards.flatMap((c) => [{ ...c, key: `${c.id}-a` }, { ...c, key: `${c.id}-b` }]);
        setDeck(shuffle(pairs));
        setFlipped([]); setMatched([]); setMoves(0);
        setCombo(0); setBestCombo(0); setScore(0); setDone(false);
        setInsight(null); setShowFireworks(false);
    };
    useEffect(() => { reset(); /* eslint-disable-next-line */ }, []);

    const click = (i) => {
        if (flipped.length >= 2) return;
        if (flipped.includes(i) || matched.includes(i)) return;
        playGameSound('flip', muted);
        const next = [...flipped, i];
        setFlipped(next);
        if (next.length === 2) {
            setMoves((m) => m + 1);
            const [a, b] = next;
            if (deck[a].id === deck[b].id) {
                setTimeout(() => {
                    setMatched((m) => [...m, a, b]);
                    setFlipped([]);
                    const newCombo = combo + 1;
                    setCombo(newCombo);
                    setBestCombo((bc) => Math.max(bc, newCombo));
                    const pts = 10 + newCombo * 5;
                    setScore((s) => s + pts);
                    playGameSound('match', muted);
                    triggerBurst();
                    announce_(newCombo > 1 ? `🔥 COMBO ×${newCombo} · +${pts}!` : `+${pts} pts`, newCombo > 1 ? 'gold' : 'green');
                    // Show insight on the matched card
                    setInsight({
                        title: deck[a].label,
                        body: deck[a].insight || deck[a].description || `Match locked in! ${FALLBACK_INSIGHTS.default}`,
                    });
                }, 500);
            } else {
                setTimeout(() => {
                    setFlipped([]);
                    setCombo(0);
                    playGameSound('wrong', muted);
                }, 900);
            }
        }
    };

    useEffect(() => {
        if (deck.length && matched.length === deck.length) {
            const movesBonus = Math.max(20, Math.round(100 - (moves - baseCards.length) * 8));
            const finalScore = Math.min(100, movesBonus);
            const points = Math.max(5, Math.round(finalScore * game.points_per_play / 100));
            setDone(true);
            setShowFireworks(true);
            setTimeout(() => setShowFireworks(false), 4500);
            playGameSound('win', muted);
            announce_(`🏆 ALL MATCHED!`, 'gold');
            api.post(`/games/${game.code}/submit`, { score: finalScore, awarded_points: points, details: { moves, bestCombo } }).then(() => onPlayed && onPlayed());
        }
        // eslint-disable-next-line
    }, [matched]);

    return (
        <GameFrame dataTestId="memory-match-game" orbs={[
            { color: '#a855f7', x: '12%', y: '20%', size: 220, delay: 0 },
            { color: '#E31E24', x: '85%', y: '18%', size: 200, delay: 1.4 },
            { color: '#3b82f6', x: '50%', y: '90%', size: 240, delay: 2.8 },
        ]}>
            <Header muted={muted} onToggleMute={() => setMuted((m) => !m)}
                moves={moves} matched={matched.length / 2} total={baseCards.length}
                combo={combo} bestCombo={bestCombo} score={score} onReset={reset} />

            <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-2.5 sm:gap-3">
                {deck.map((c, i) => {
                    const isOpen = flipped.includes(i) || matched.includes(i);
                    const isMatched = matched.includes(i);
                    return (
                        <button key={c.key} onClick={() => click(i)} className="aspect-[3/4] relative perspective-[800px]"
                            data-testid={`mem-card-${i}`}>
                            <motion.div
                                animate={{ rotateY: isOpen ? 180 : 0, scale: isMatched ? 0.95 : 1 }}
                                transition={{ duration: 0.5 }}
                                className="relative w-full h-full"
                                style={{ transformStyle: 'preserve-3d' }}
                            >
                                {/* Card back — holographic gradient with UT mark */}
                                <div className="absolute inset-0 rounded-2xl overflow-hidden ring-2 ring-white/30" style={{ backfaceVisibility: 'hidden' }}>
                                    <div className="absolute inset-0"
                                        style={{ background: 'linear-gradient(135deg, #FFF200 0%, #F59E0B 25%, #E31E24 50%, #7C3AED 75%, #3B82F6 100%)', backgroundSize: '200% 200%' }} />
                                    <motion.div animate={{ backgroundPosition: ['0% 0%', '100% 100%'] }} transition={{ repeat: Infinity, duration: 6, ease: 'linear' }}
                                        className="absolute inset-0 opacity-60"
                                        style={{ background: 'linear-gradient(135deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)', backgroundSize: '200% 200%' }} />
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="text-white font-display font-extrabold text-2xl drop-shadow-lg">UT</div>
                                    </div>
                                    <div className="absolute inset-1 rounded-2xl border border-white/30 pointer-events-none" />
                                </div>
                                {/* Card front — content */}
                                <motion.div
                                    animate={isMatched ? { boxShadow: ['0 0 0 0 rgba(16,185,129,0)', '0 0 0 8px rgba(16,185,129,0.4)', '0 0 0 0 rgba(16,185,129,0)'] } : {}}
                                    transition={isMatched ? { repeat: Infinity, duration: 1.6 } : {}}
                                    className={`absolute inset-0 rounded-2xl flex flex-col items-center justify-center p-2 text-center ring-2 ${isMatched ? 'ring-emerald-400' : 'ring-white/50'}`}
                                    style={{
                                        backfaceVisibility: 'hidden', transform: 'rotateY(180deg)',
                                        background: isMatched
                                            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                            : 'linear-gradient(135deg, #fef3c7 0%, #fbbf24 50%, #f59e0b 100%)',
                                    }}>
                                    <div className={`text-xs sm:text-sm font-display font-extrabold ${isMatched ? 'text-white' : 'text-slate-900'}`}>{c.label}</div>
                                    {c.icon && <div className="text-2xl mt-1">{c.icon}</div>}
                                    {isMatched && <Sparkles className="absolute top-1 right-1 h-3.5 w-3.5 text-yellow-200" />}
                                </motion.div>
                            </motion.div>
                        </button>
                    );
                })}
            </div>

            {/* Bottom info / replay */}
            {done && (
                <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                    className="mt-5 rounded-3xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white p-5 text-center" data-testid="memory-done">
                    <Trophy className="h-10 w-10 mx-auto text-amber-300" />
                    <div className="font-display font-extrabold text-2xl mt-2">All pairs matched!</div>
                    <div className="text-sm opacity-90 mt-1">{moves} moves · Best combo ×{bestCombo} · {score} pts earned</div>
                    <div className="mt-4 flex justify-center">
                        <GoldButton onClick={reset} dataTestId="memory-reset-done">
                            <RefreshCw className="h-4 w-4" /> Play Again
                        </GoldButton>
                    </div>
                </motion.div>
            )}

            {bursts.map((b) => <ConfettiBurst key={b.id} count={b.count} scale={b.scale} />)}
            {showFireworks && <Fireworks duration={4.5} density={1.5} />}
            <AnnouncementBanner announce={announce} />

            {/* Insight modal — appears briefly per match */}
            <AnimatePresence>
                {insight && (
                    <motion.div key={insight.title}
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
                        onClick={() => setInsight(null)}>
                        <motion.div initial={{ scale: 0.8, y: 20 }} animate={{ scale: 1, y: 0 }}
                            className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
                            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white p-4 flex items-center gap-3">
                                <Zap className="h-6 w-6" />
                                <div className="font-display font-extrabold text-lg">Match Locked!</div>
                            </div>
                            <div className="p-5">
                                <div className="text-[10px] uppercase tracking-wide font-bold text-emerald-600 mb-1 inline-flex items-center gap-1"><Lightbulb className="h-3 w-3" />Did you know?</div>
                                <div className="font-display font-bold text-slate-900">{insight.title}</div>
                                <p className="text-sm text-slate-700 mt-1 leading-relaxed">{insight.body}</p>
                                <button onClick={() => setInsight(null)} className="mt-4 w-full px-4 py-2.5 rounded-2xl bg-slate-900 text-white font-semibold" data-testid="memory-insight-continue">Continue →</button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </GameFrame>
    );
}

function Header({ muted, onToggleMute, moves, matched, total, combo, bestCombo, score, onReset }) {
    return (
        <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-rose-500 to-fuchsia-600 flex items-center justify-center"
                style={{ boxShadow: '0 0 20px rgba(244,63,94,0.45)' }}>
                <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-rose-300">Propulse Cables & Wires</div>
                <div className="font-display font-extrabold text-lg sm:text-2xl text-white truncate">Memory Match</div>
                <div className="text-[11px] sm:text-xs text-slate-400 truncate">Flip pairs · build combos · earn bonus points</div>
            </div>
            <div className="hidden sm:flex gap-2">
                <ScoreTile label="Pts" value={score} accent="from-amber-400 to-yellow-500" />
                <div data-testid="memory-pairs"><ScoreTile label="Pairs" value={`${matched}/${total}`} accent="from-emerald-400 to-emerald-600" /></div>
                <div data-testid="memory-combo"><ScoreTile label="Combo" value={`×${combo}`} accent="from-rose-400 to-rose-600" /></div>
            </div>
            <button onClick={onReset} className="w-9 h-9 rounded-xl bg-white/10 text-white flex items-center justify-center hover:bg-white/20" data-testid="memory-reset-btn"
                title="Reset"><RefreshCw className="h-4 w-4" /></button>
            <button onClick={onToggleMute} className="w-9 h-9 rounded-xl bg-white/10 text-white flex items-center justify-center hover:bg-white/20" data-testid="memory-mute-btn">
                {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
            <div className="sm:hidden flex gap-2 absolute right-0 top-16 hidden">
                {/* mobile scores below header */}
            </div>
        </div>
    );
}
